'use client';

import { std } from '@funkey/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '~/modules/trpc/client';
import { contextFactory, useCookie } from '~/utils/react';
import { CART_COOKIE_BASE_OPTIONS, CART_COOKIE_NAME } from './shared';

export const [useCartCtx, CartCtxProvider] = contextFactory(() => {
    const trpc = useTRPC();
    const cookie = useCookie(CART_COOKIE_NAME, CART_COOKIE_BASE_OPTIONS);

    const qc = useQueryClient();

    /** Refetch all queries starting with the `QK_PREFIX`.
     * Call this function when cart has been updated and all data should be refetched */
    async function invalidateQueries() {
        return qc.invalidateQueries({ exact: false, queryKey: trpc.cart.pathKey() });
    }

    return {
        /** Client-side mirror of the current cart cookie. */
        id: std.Number.safeParseInt(cookie.state),
        /**
         * Sync the browser-visible cart cookie and local React state.
         *
         * This does NOT establish server request context by itself; mutations that create a cart
         * must still set `Set-Cookie` on the HTTP response so the next request reliably carries it.
         */
        syncID(cartID: number | null) {
            cookie.setState(cartID === null ? null : cartID.toString());
        },
        invalidateQueries,
    } as const;
});
